import { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  Field,
  FieldLabel,
  Flex,
  Select,
  SelectContent,
  SelectTrigger,
  Textarea,
} from '@optiaxiom/react';

import { CompanionRule, CreateRuleRequest, UpdateRuleRequest } from '@frontend/types/app-companion';

import { eventTypes, productTypes } from '../constants';

import { EventInformation } from './EventInformation';

type RuleDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (rule: CreateRuleRequest | UpdateRuleRequest) => void;
  agentId: string;
  editRule?: CompanionRule | null;
};

export function RuleDialog({ open, onOpenChange, onSubmit, agentId, editRule }: RuleDialogProps) {
  const isEditMode = !!editRule;

  const [formData, setFormData] = useState<CreateRuleRequest>({
    event_type: '',
    agent_id: agentId,
    description: '',
  });
  const [product, setProduct] = useState(() =>
    formData.event_type ? formData.event_type.split('.')[0] : ''
  );

  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (editRule) {
      setFormData({
        event_type: editRule.event_type,
        agent_id: editRule.agent_id,
        description: editRule.description || '',
      });
      setProduct(editRule.event_type.split('.')[0]);
    } else {
      setFormData({
        event_type: '',
        agent_id: agentId,
        description: '',
      });
      setProduct('');
    }
  }, [editRule, agentId]);

  const handleSubmit = async () => {
    if (!formData.event_type) {
      return;
    }

    setIsSubmitting(true);
    try {
      await onSubmit(formData);
      setFormData({
        event_type: '',
        agent_id: agentId,
        description: '',
      });
      onOpenChange(false);
    } catch (error) {
      console.error('Failed to create rule:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (!isEditMode) {
      setFormData({
        event_type: '',
        agent_id: agentId,
        description: '',
      });
    }
    onOpenChange(false);
  };

  const isValid = formData.event_type;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="sm">
        <DialogHeader>{isEditMode ? 'Edit Rule' : 'Add Rule'}</DialogHeader>
        <DialogBody>
          <Flex flexDirection="column" gap="16">
            <Field>
              <FieldLabel required>Product</FieldLabel>
              <Select
                required
                onValueChange={value => setProduct(value)}
                options={productTypes}
                value={product}
              >
                <SelectTrigger placeholder="Select a product" />
                <SelectContent />
              </Select>
            </Field>

            <Field>
              <FieldLabel required>Event Type</FieldLabel>

              <Select
                value={formData.event_type}
                onValueChange={value => setFormData({ ...formData, event_type: value })}
                options={eventTypes
                  .filter(eventType => eventType.product === product)
                  .map(eventType => ({
                    label: eventType.label,
                    value: eventType.value,
                  }))}
                required
              >
                <SelectTrigger placeholder="Select an event" />
                <SelectContent />
              </Select>
            </Field>

            <Field>
              <FieldLabel>Description</FieldLabel>
              <Textarea
                placeholder="Enter rule description (optional)"
                value={formData.description}
                onChange={e => setFormData({ ...formData, description: e.target.value })}
                rows={3}
              />
            </Field>

            <EventInformation eventType={formData.event_type} />
          </Flex>
        </DialogBody>
        <DialogFooter>
          <Button appearance="subtle" onClick={handleCancel} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button appearance="primary" onClick={handleSubmit} disabled={!isValid || isSubmitting}>
            {isSubmitting
              ? isEditMode
                ? 'Updating...'
                : 'Creating...'
              : isEditMode
                ? 'Update Rule'
                : 'Create Rule'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
